import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polygon, Polyline, Circle, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import html2canvas from 'html2canvas';
import { Camera, Palette, X as IconX, Maximize, Minimize, MapPin, ArrowLeftRight, Waypoints, Check, FileDown, Loader2 } from 'lucide-react';
import { exportCoverageProjectPdf } from '../../utils/coverageProjectExport';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// Import leaflet images from package to avoid 'Tracking Prevention' blocking unpkg
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix for default marker icons in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl,
    iconUrl,
    shadowUrl,
});

const RecenterMap = ({ coords }) => {
    const map = useMap();
    useEffect(() => {
        if (coords) {
            map.flyTo(coords, 16);
        }
    }, [coords, map]);
    return null;
};

// Componente para capturar eventos do mapa (Right Click)
const MapEvents = ({ onContextMenu }) => {
    useMapEvents({
        contextmenu(e) {
            onContextMenu(e);
        },
    });
    return null;
};

// Expõe a instância do mapa Leaflet ao componente pai (usado para enquadrar o
// drop/CTO/cliente antes de tirar o screenshot de exportação)
const MapInstanceCapture = ({ mapInstanceRef }) => {
    const map = useMap();
    useEffect(() => {
        mapInstanceRef.current = map;
    }, [map, mapInstanceRef]);
    return null;
};

// Distância euclidiana (ao quadrado) de um ponto até um segmento, em graus lat/lng.
// Suficiente para decidir em qual trecho da linha um novo nó deve ser inserido (escala local pequena).
const pointToSegmentDistSq = (p, a, b) => {
    const [px, py] = p, [ax, ay] = a, [bx, by] = b;
    const dx = bx - ax, dy = by - ay;
    if (dx === 0 && dy === 0) return (px - ax) ** 2 + (py - ay) ** 2;
    let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    return (px - cx) ** 2 + (py - cy) ** 2;
};

// Aguarda os tiles do mapa (imagens) terminarem de carregar antes de tirar o screenshot,
// evitando exportar um mapa parcialmente cinza. Resolve mais cedo se todos já carregaram,
// ou após maxWaitMs como teto de segurança.
const waitForTilesLoaded = (container, maxWaitMs = 4000) => {
    return new Promise((resolve) => {
        if (!container) { resolve(); return; }
        const start = Date.now();
        const check = () => {
            const tiles = container.querySelectorAll('.leaflet-tile');
            const allLoaded = Array.from(tiles).every(img => img.complete && img.naturalWidth > 0);
            if (allLoaded || Date.now() - start > maxWaitMs) {
                resolve();
            } else {
                setTimeout(check, 150);
            }
        };
        check();
    });
};

// Afasta verticalmente as etiquetas permanentes de caixas que ficam sobrepostas no
// screenshot de exportação (caixas próximas geograficamente projetam labels próximos
// na tela). Empurra a etiqueta mais à direita/abaixo para cima até não colidir mais
// com nenhuma etiqueta já resolvida, encadeando o efeito quando há várias sobrepostas.
const declutterExportLabels = (container) => {
    if (!container) return;
    const labels = Array.from(container.querySelectorAll('.coverage-box-label'));
    if (labels.length < 2) return;

    const containerRect = container.getBoundingClientRect();
    const GAP = 3;
    const MAX_PASSES = labels.length * 4;

    const boxes = labels.map((el) => {
        const rect = el.getBoundingClientRect();
        return {
            el,
            top: rect.top - containerRect.top,
            left: rect.left - containerRect.left,
            width: rect.width,
            height: rect.height,
            offset: 0,
        };
    });

    // Resolve da esquerda para a direita para obter um resultado determinístico
    boxes.sort((a, b) => a.left - b.left || a.top - b.top);

    const currentTop = (box) => box.top - box.offset;
    const overlaps = (a, b) => {
        const aTop = currentTop(a);
        const bTop = currentTop(b);
        return a.left < b.left + b.width &&
               a.left + a.width > b.left &&
               aTop < bTop + b.height &&
               aTop + a.height > bTop;
    };

    for (let i = 1; i < boxes.length; i++) {
        const current = boxes[i];
        let passes = 0;
        let moved = true;
        while (moved && passes < MAX_PASSES) {
            moved = false;
            passes += 1;
            for (let j = 0; j < i; j++) {
                const other = boxes[j];
                if (overlaps(current, other)) {
                    const overlapAmount = (currentTop(current) + current.height) - currentTop(other) + GAP;
                    current.offset += overlapAmount;
                    moved = true;
                }
            }
        }
    }

    boxes.forEach(({ el, offset }) => {
        if (offset > 0.5) {
            el.style.marginTop = `-${offset}px`;
        }
    });
};

// Ícone pequeno e arrastável usado para os nós de edição de traçado.
// O cursor (grab/pointer) é controlado via CSS pela classe "shift-delete-mode" no wrapper (ver <style> abaixo),
// para refletir dinamicamente a tecla Shift sem precisar recriar o ícone a cada tecla pressionada.
const editNodeIcon = new L.divIcon({
    html: `<div class="edit-node-dot" style="width:14px;height:14px;border-radius:50%;background:#a855f7;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:grab;"></div>`,
    className: 'edit-node-icon',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

const CoverageMap = ({ userLocation, mapData, viable, theme, darkMapEnabled = false, nearestCto, address, onRequestStreetView, exportedBy }) => {
    // Default center (can be Brazil or a specific city)
    const defaultCenter = [-22.704528, -48.357556]; // Botucatu center example
    const center = userLocation || defaultCenter;

    // Local theme state for "Trocar modo de Cor"

    const [route, setRoute] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Estado para "Trocar CTO" / "Alterar traçado" do drop
    const [routeMode, setRouteMode] = useState('idle'); // 'idle' | 'swap' | 'edit'
    const [manualCto, setManualCto] = useState(null); // CTO escolhida manualmente via "Trocar CTO"
    const [manualRoute, setManualRoute] = useState(null); // Traçado fixado via "Alterar traçado"
    const [editNodes, setEditNodes] = useState([]); // Nós intermediários enquanto edita o traçado
    const [lineContextMenu, setLineContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const [exportMode, setExportMode] = useState(false); // Ativa etiquetas permanentes para o screenshot do PDF
    const [isExporting, setIsExporting] = useState(false);
    const [shiftHeld, setShiftHeld] = useState(false); // Shift pressionado: clique em um nó o exclui

    const effectiveCto = manualCto || nearestCto;

    // A busca de cobertura pode retornar a mesma caixa mais de uma vez quando
    // ela pertence a respostas de projetos/raios sobrepostos. Cada ocorrência
    // geraria um Tooltip permanente no mapa e, consequentemente, uma legenda
    // repetida no mapa exportado.
    const uniqueBoxes = useMemo(() => {
        const seen = new Set();
        return (mapData?.boxes || []).filter((box) => {
            const id = box.id || box._id;
            const lat = Number(box.coords?.lat ?? box.lat);
            const lng = Number(box.coords?.lng ?? box.lng);
            const positionKey = Number.isFinite(lat) && Number.isFinite(lng)
                ? `${lat.toFixed(6)}:${lng.toFixed(6)}`
                : '';
            // A mesma caixa pode vir com IDs diferentes ao cruzar projetos;
            // quando há coordenadas, elas são a identidade física mais segura.
            const key = positionKey
                ? `position:${box.name || ''}:${positionKey}`
                : `id:${id || box.name || ''}`;

            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [mapData?.boxes]);

    // Reseta customizações (CTO manual / traçado editado) sempre que uma nova busca é feita
    useEffect(() => {
        setManualCto(null);
        setManualRoute(null);
        setEditNodes([]);
        setRouteMode('idle');
    }, [userLocation, nearestCto]);

    // Rastreia a tecla Shift para permitir excluir nós do traçado em edição
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Shift') setShiftHeld(true); };
        const handleKeyUp = (e) => { if (e.key === 'Shift') setShiftHeld(false); };
        const handleBlur = () => setShiftHeld(false);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    // Traça o caminho do cabo ligando a CTO até o cliente respeitando o traçado das ruas
    useEffect(() => {
        if (manualRoute) return; // Traçado fixado manualmente: não sobrescreve
        if (userLocation && effectiveCto && effectiveCto.coords) {
            const fetchRoute = async () => {
                try {
                    // OSRM expects longitude,latitude
                    const startLon = effectiveCto.coords.lng;
                    const startLat = effectiveCto.coords.lat;
                    const endLon = userLocation[1];
                    const endLat = userLocation[0];

                    const response = await fetch(`https://router.project-osrm.org/route/v1/foot/${startLon},${startLat};${endLon},${endLat}?overview=full&geometries=geojson`);
                    const data = await response.json();

                    if (data.routes && data.routes.length > 0) {
                        const routeDistance = data.routes[0].distance;
                        const straightLineDistance = L.latLng([startLat, startLon]).distanceTo(L.latLng([endLat, endLon]));

                        // Se a distância em linha reta for muito curta (< 50m) ou se a rota sugerida pelo OSRM for
                        // desproporcionalmente longa (> 3x a linha reta), ignora o OSRM e traça uma linha reta.
                        // Isso previne que o "snapping" do OSRM em ruas paralelas crie voltas no quarteirão.
                        if (straightLineDistance < 50 || routeDistance > straightLineDistance * 3) {
                            setRoute([ [startLat, startLon], userLocation ]);
                        } else {
                            // OSRM returns coordinates as [longitude, latitude]
                            // Leaflet Polyline expects [latitude, longitude]
                            const coords = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);

                            // Conecta as pontas exatas (CTO e Cliente) aos pontos iniciais/finais da rua para evitar "gaps" visuais
                            coords.unshift([startLat, startLon]);
                            coords.push([endLat, endLon]);

                            setRoute(coords);
                        }
                    } else {
                         setRoute([ [startLat, startLon], userLocation ]);
                    }
                } catch (error) {
                    console.error("Erro ao buscar rota OSRM:", error);
                    setRoute([ [effectiveCto.coords.lat, effectiveCto.coords.lng], userLocation ]);
                }
            };
            fetchRoute();
        } else {
            setRoute(null);
        }
    }, [userLocation, effectiveCto, manualRoute]);
const [localTheme, setLocalTheme] = useState(null); // null means follow prop 'theme'

    const isGlobalDark = theme === 'dark' && darkMapEnabled;
    const isDark = darkMapEnabled && (localTheme !== null ? localTheme === 'dark' : isGlobalDark);

    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const [feedback, setFeedback] = useState({ visible: false, message: '', x: 0, y: 0 });
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
        // Dispatch window resize event after state change so leaflet invalidates size
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 100);
    };

    // Tile Layer based on theme
    const tileUrl = isDark
        ? getMapTileUrl(true)
        : getMapTileUrl();

    const attribution = getMapTileAttribution(isDark);

    // Custom Icons using colored markers
    const userIconHtml = `
        <div style="
            background-color: ${viable ? '#22c55e' : '#ef4444'};
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
        </div>
    `;

    const userIcon = new L.divIcon({
        html: userIconHtml,
        className: 'custom-user-icon',
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        popupAnchor: [0, -15],
    });

    const boxIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
        shadowUrl: shadowUrl,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
    });

    const nearestBoxIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
        shadowUrl: shadowUrl,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
    });

    const handleContextMenu = (e) => {
        setContextMenu({
            visible: true,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
            latlng: e.latlng
        });
    };

    const closeContextMenu = () => {
        setContextMenu(prev => ({ ...prev, visible: false }));
    };

    const closeLineContextMenu = () => {
        setLineContextMenu(prev => ({ ...prev, visible: false }));
    };

    const handleLineContextMenu = (e) => {
        L.DomEvent.stopPropagation(e);
        setLineContextMenu({
            visible: true,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
        });
    };

    const startSwapCto = () => {
        setRouteMode('swap');
        closeLineContextMenu();
    };

    const startEditRoute = () => {
        const currentPath = manualRoute || route;
        // Usa os pontos intermediários da rota atual (excluindo as pontas CTO/cliente) como nós iniciais
        setEditNodes(currentPath && currentPath.length > 2 ? currentPath.slice(1, -1) : []);
        setRouteMode('edit');
        closeLineContextMenu();
    };

    const cancelRouteMode = () => {
        setRouteMode('idle');
        setEditNodes([]);
    };

    const handleCtoSelect = (box) => {
        if (routeMode !== 'swap') return;
        setManualCto({ name: box.name, coords: box.coords });
        setManualRoute(null);
        setEditNodes([]);
        setRouteMode('idle');
    };

    const handleRouteLineClick = (e) => {
        if (routeMode !== 'edit' || !effectiveCto || !userLocation) return;
        L.DomEvent.stopPropagation(e);
        const startPoint = [effectiveCto.coords.lat, effectiveCto.coords.lng];
        const clickPt = [e.latlng.lat, e.latlng.lng];
        const fullPath = [startPoint, ...editNodes, userLocation];

        let bestIdx = 0;
        let bestDist = Infinity;
        for (let i = 0; i < fullPath.length - 1; i++) {
            const d = pointToSegmentDistSq(clickPt, fullPath[i], fullPath[i + 1]);
            if (d < bestDist) {
                bestDist = d;
                bestIdx = i;
            }
        }
        setEditNodes(prev => {
            const next = [...prev];
            next.splice(bestIdx, 0, clickPt);
            return next;
        });
    };

    const handleNodeDrag = (index, latlng) => {
        setEditNodes(prev => {
            const next = [...prev];
            next[index] = [latlng.lat, latlng.lng];
            return next;
        });
    };

    const handleNodeClick = (index, e) => {
        if (!shiftHeld) return;
        L.DomEvent.stopPropagation(e);
        setEditNodes(prev => prev.filter((_, i) => i !== index));
    };

    const finishEditRoute = () => {
        if (!effectiveCto || !userLocation) return;
        const startPoint = [effectiveCto.coords.lat, effectiveCto.coords.lng];
        setManualRoute([startPoint, ...editNodes, userLocation]);
        setRouteMode('idle');
    };

    const toggleMapMode = () => {
        setLocalTheme(prev => {
            if (prev === null) return isGlobalDark ? 'light' : 'dark';
            return prev === 'dark' ? 'light' : 'dark';
        });
        closeContextMenu();
    };

    const showFeedbackMessage = (message, x, y) => {
        setFeedback({ visible: true, message, x, y });
        setTimeout(() => setFeedback(prev => ({ ...prev, visible: false })), 2000);
    };

    const handleStreetView = () => {
        if (contextMenu.latlng && onRequestStreetView) {
            onRequestStreetView({
                lat: contextMenu.latlng.lat,
                lng: contextMenu.latlng.lng
            });
            if (isFullscreen) {
                toggleFullscreen();
            }
        }
        closeContextMenu();
    };

    const handleScreenshot = async () => {
        if (!mapRef.current) return;
        closeContextMenu();

        // 1. Cria o overlay de informações para o screenshot
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.bottom = '20px';
        overlay.style.left = '20px';
        overlay.style.right = '20px';
        overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        overlay.style.padding = '10px 14px';
        overlay.style.borderRadius = '8px';
        overlay.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        overlay.style.borderLeft = '4px solid #f97316';
        overlay.style.zIndex = '9999';
        overlay.style.fontFamily = 'Inter, system-ui, -apple-system, sans-serif';
        overlay.style.maxWidth = '450px';
        overlay.style.boxSizing = 'border-box';

        const title = document.createElement('h3');
        title.innerText = 'Consulta de Viabilidade';
        title.style.margin = '0 0 6px 0';
        title.style.color = '#1e293b';
        title.style.fontSize = '14px';
        title.style.fontWeight = 'bold';
        overlay.appendChild(title);

        const addInfo = (label, value) => {
            const p = document.createElement('p');
            p.style.margin = '2px 0';
            p.style.color = '#475569';
            p.style.fontSize = '11px';
            p.style.lineHeight = '1.4';

            const strong = document.createElement('strong');
            strong.textContent = `${label}:`;
            p.appendChild(strong);

            const textNode = document.createTextNode(` ${value || 'N/A'}`);
            p.appendChild(textNode);

            overlay.appendChild(p);
        };

        const coordsText = userLocation ? `${userLocation[0].toFixed(6)}, ${userLocation[1].toFixed(6)}` : 'N/A';
        const ctoName = nearestCto ? nearestCto.name : 'Não identificada';
        const ctoDist = nearestCto ? nearestCto.distance : '';

        addInfo('Endereço', address);
        addInfo('Coordenadas', coordsText);
        addInfo('CTO Mais Próxima', `${ctoName} ${ctoDist ? '(' + ctoDist + ')' : ''}`);

        // Insere o overlay no container do mapa
        mapRef.current.appendChild(overlay);

        // Aguarda a renderização completa dos tiles e vetores canvas pelo Leaflet.
        // O delay de 500ms garante que os tiles do mapa estejam carregados e que o
        // renderer Canvas do Leaflet (preferCanvas) tenha desenhado todos os vetores.
        await new Promise(r => setTimeout(r, 500));

        // 2. Captura o mapa com html2canvas
        try {
            const canvas = await html2canvas(mapRef.current, {
                useCORS: true,
                allowTaint: true,
                logging: false,
                // Ignora controles de zoom para um screenshot mais limpo
                ignoreElements: (element) => {
                    return element.classList.contains('leaflet-control-zoom') ||
                           element.classList.contains('leaflet-control-attribution');
                }
            });

            // 3. Remove o overlay após a captura
            if (mapRef.current.contains(overlay)) {
                mapRef.current.removeChild(overlay);
            }

            // 4. Copia a imagem para a área de transferência
            canvas.toBlob((blob) => {
                if (!blob) return;
                try {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]).then(() => {
                        showFeedbackMessage('Screenshot copiado!', contextMenu.x, contextMenu.y);
                    });
                } catch (err) {
                    console.error('Falha ao copiar imagem:', err);
                    alert('Não foi possível copiar a imagem. Tente baixar.');
                }
            });

        } catch (error) {
            console.error('Erro ao tirar screenshot:', error);
            if (mapRef.current.contains(overlay)) {
                mapRef.current.removeChild(overlay);
            }
        }
    };

    const handleExportProject = async () => {
        closeContextMenu();
        if (!mapRef.current || !userLocation || !effectiveCto) {
            alert('Selecione um endereço e uma CTO antes de exportar o projeto.');
            return;
        }

        const map = mapInstanceRef.current;
        const displayRoute = manualRoute || route;
        let dropMeters = null;
        if (displayRoute && displayRoute.length > 1) {
            dropMeters = 0;
            for (let i = 0; i < displayRoute.length - 1; i++) {
                dropMeters += L.latLng(displayRoute[i]).distanceTo(L.latLng(displayRoute[i + 1]));
            }
        }

        setIsExporting(true);
        const previousLocalTheme = localTheme;
        const previousCenter = map ? map.getCenter() : null;
        const previousZoom = map ? map.getZoom() : null;
        setLocalTheme('light'); // Exportação sempre em modo claro
        setExportMode(true); // Ativa etiquetas permanentes nas caixas

        try {
            // Enquadra o mapa no drop (CTO + traçado + cliente), com folga, para que a
            // exportação sempre mostre os elementos de perto — independente do zoom/pan
            // em que o usuário deixou o mapa ao vivo.
            if (map) {
                const boundsPoints = [
                    userLocation,
                    [effectiveCto.coords.lat, effectiveCto.coords.lng],
                    ...(displayRoute || []),
                ];
                const bounds = L.latLngBounds(boundsPoints);
                map.fitBounds(bounds, { padding: [60, 60], maxZoom: 19, animate: false });
                map.invalidateSize();
            }

            // Aguarda o re-render (tema claro + etiquetas + novo enquadramento) antes de aguardar os tiles
            await new Promise(r => setTimeout(r, 350));
            // Aguarda os tiles do mapa carregarem de fato (com um teto de segurança)
            await waitForTilesLoaded(mapRef.current, 4000);
            // Pequena folga para o renderer Canvas do Leaflet (preferCanvas) desenhar os vetores
            await new Promise(r => setTimeout(r, 300));
            // Afasta etiquetas de caixas próximas que ficariam sobrepostas no screenshot
            declutterExportLabels(mapRef.current);

            const canvas = await html2canvas(mapRef.current, {
                useCORS: true,
                allowTaint: true,
                logging: false,
                ignoreElements: (element) => {
                    return element.classList.contains('leaflet-control-zoom') ||
                           element.classList.contains('leaflet-control-attribution');
                }
            });
            const mapImageDataUrl = canvas.toDataURL('image/png');
            const mapImageWidth = canvas.width;
            const mapImageHeight = canvas.height;

            await exportCoverageProjectPdf({
                mapImageDataUrl,
                mapImageWidth,
                mapImageHeight,
                address,
                clientCoords: { lat: userLocation[0], lng: userLocation[1] },
                cto: effectiveCto,
                dropMeters,
                exportedBy,
            });
        } catch (error) {
            console.error('Erro ao exportar projeto em PDF:', error);
            alert('Não foi possível gerar o PDF do projeto.');
        } finally {
            setExportMode(false);
            setLocalTheme(previousLocalTheme);
            if (map && previousCenter && previousZoom != null) {
                map.setView(previousCenter, previousZoom, { animate: false });
            }
            setIsExporting(false);
        }
    };

    const wrapperClasses = (isFullscreen
        ? "fixed inset-0 z-50 bg-white dark:bg-slate-900"
        : "h-full w-full rounded-xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-700 relative z-0")
        + (routeMode === 'edit' && shiftHeld ? " shift-delete-mode" : "");

    return (
        <div className={wrapperClasses} ref={mapRef}>
             {isFullscreen && (
                 <button
                     onClick={toggleFullscreen}
                     className="absolute top-4 right-4 z-[9999] bg-white dark:bg-slate-800 p-2 rounded-full shadow-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                     title="Sair de Tela Cheia"
                 >
                     <Minimize className="w-6 h-6" />
                 </button>
             )}
             {!isFullscreen && (
                 <button
                     onClick={toggleFullscreen}
                     className="absolute top-4 right-4 z-[9999] bg-white dark:bg-slate-800 p-2 rounded-full shadow-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                     title="Tela Cheia"
                 >
                     <Maximize className="w-5 h-5" />
                 </button>
             )}
             <style>
                 {`
                 .pulsing-route {
                     animation: pulseGlow 1.5s infinite alternate ease-in-out;
                     filter: drop-shadow(0 0 8px rgba(249, 115, 22, 0.8));
                     stroke-dashoffset: 0;
                 }
                 @keyframes pulseGlow {
                     0% {
                         filter: drop-shadow(0 0 3px rgba(249, 115, 22, 0.5));
                         opacity: 0.7;
                         stroke-width: 4;
                     }
                     100% {
                         filter: drop-shadow(0 0 12px rgba(249, 115, 22, 1));
                         opacity: 1;
                         stroke-width: 6;
                     }
                 }
                 .pulsing-swap {
                     animation: pulseGlowSwap 1s infinite alternate ease-in-out;
                     filter: drop-shadow(0 0 8px rgba(236, 72, 153, 0.9));
                 }
                 @keyframes pulseGlowSwap {
                     0% {
                         filter: drop-shadow(0 0 3px rgba(236, 72, 153, 0.6));
                         opacity: 0.6;
                     }
                     100% {
                         filter: drop-shadow(0 0 14px rgba(236, 72, 153, 1));
                         opacity: 1;
                     }
                 }
                 .editing-route {
                     filter: drop-shadow(0 0 6px rgba(168, 85, 247, 0.8));
                 }
                 .cto-selectable {
                     animation: pulseCtoSelectable 1s infinite alternate ease-in-out;
                     cursor: pointer;
                 }
                 @keyframes pulseCtoSelectable {
                     0% { filter: drop-shadow(0 0 2px rgba(236, 72, 153, 0.6)); }
                     100% { filter: drop-shadow(0 0 10px rgba(236, 72, 153, 1)); }
                 }
                 .shift-delete-mode .leaflet-container,
                 .shift-delete-mode .edit-node-icon .edit-node-dot {
                     cursor: pointer !important;
                 }
                 /* Etiqueta das caixas na exportação em PDF: estilo próprio, sem a seta/box-shadow
                    padrão do Leaflet Tooltip (o html2canvas não renderiza bem pseudo-elementos e
                    sombras, gerando um contorno cinza deslocado e desalinhamento do texto).
                    Centralização vertical via line-height + padding simétrico (não via flexbox:
                    o html2canvas 1.4.x não respeita align-items corretamente e empurra o texto
                    para a base da etiqueta). */
                 .coverage-box-label {
                     background: #ffffff !important;
                     border: 1px solid #94a3b8 !important;
                     border-radius: 4px !important;
                     box-shadow: none !important;
                     color: #1e293b !important;
                     font-size: 11px !important;
                     font-weight: 600 !important;
                     line-height: 14px !important;
                     padding: 4px 7px !important;
                     white-space: nowrap !important;
                 }
                 .coverage-box-label::before {
                     display: none !important;
                 }
                 `}
             </style>
             <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
                <MapEvents onContextMenu={handleContextMenu} />
                <MapInstanceCapture mapInstanceRef={mapInstanceRef} />
                <TileLayer
                    url={tileUrl}
                    attribution={attribution}
                />

                {userLocation && <RecenterMap coords={userLocation} />}


                {/* Rota (drop) até a CTO selecionada */}
                {(() => {
                    const displayRoute = routeMode === 'edit' && effectiveCto && userLocation
                        ? [[effectiveCto.coords.lat, effectiveCto.coords.lng], ...editNodes, userLocation]
                        : (manualRoute || route);
                    if (!displayRoute) return null;

                    let pathOptions = { color: '#f97316', weight: 4, opacity: 1, dashArray: '5, 10', className: 'pulsing-route' };
                    if (routeMode === 'swap') {
                        pathOptions = { color: '#ec4899', weight: 4, opacity: 1, dashArray: '5, 10', className: 'pulsing-swap' };
                    } else if (routeMode === 'edit') {
                        pathOptions = { color: '#a855f7', weight: 4, opacity: 1, dashArray: '8, 6', className: 'editing-route' };
                    }

                    return (
                        <Polyline
                            positions={displayRoute}
                            pathOptions={pathOptions}
                            eventHandlers={{
                                contextmenu: handleLineContextMenu,
                                click: handleRouteLineClick,
                            }}
                        >
                            {routeMode === 'idle' && (
                                <Popup>
                                    <strong>Drop até a CTO</strong><br />
                                    {effectiveCto?.name}
                                </Popup>
                            )}
                        </Polyline>
                    );
                })()}

                {/* Nós arrastáveis do traçado em edição */}
                {routeMode === 'edit' && editNodes.map((node, idx) => (
                    <Marker
                        key={`edit-node-${idx}`}
                        position={node}
                        icon={editNodeIcon}
                        draggable={!shiftHeld}
                        eventHandlers={{
                            drag: (e) => handleNodeDrag(idx, e.target.getLatLng()),
                            dragend: (e) => handleNodeDrag(idx, e.target.getLatLng()),
                            click: (e) => handleNodeClick(idx, e),
                        }}
                    />
                ))}
{/* User Location Marker */}
                {userLocation && (
                    <>
                        <Marker position={userLocation} icon={userIcon}>
                            <Popup>
                                <strong>Local Pesquisado</strong><br/>
                                <span className="text-xs">{address || 'Endereço não informado'}</span><br/>
                                <span className="text-xs text-slate-500">
                                    {userLocation[0].toFixed(6)}, {userLocation[1].toFixed(6)}
                                </span>
                            </Popup>
                        </Marker>
                        <Circle
                            center={userLocation}
                            radius={200}
                            pathOptions={{ color: viable ? 'green' : 'red', fillColor: viable ? 'green' : 'red', fillOpacity: 0.1 }}
                        />
                    </>
                )}

                {/* Render Sectors (Polygons) */}
                {mapData && mapData.sectors && mapData.sectors.map((sector, index) => {
                     // Ensure coords is array of {lat, lng} or similar
                     if (!sector.coords || !Array.isArray(sector.coords)) return null;
                     const positions = sector.coords.map(c => [c.lat, c.lng]);

                     return (
                         <Polygon
                            key={`sector-${index}`}
                            positions={positions}
                            pathOptions={{ color: 'purple', fillColor: 'purple', fillOpacity: 0.1, weight: 1 }}
                         >
                            <Popup>
                                <strong>Setor:</strong> {sector.name}<br/>
                                {sector.olt && <span><strong>OLT:</strong> {sector.olt.name}</span>}
                            </Popup>
                         </Polygon>
                     );
                })}

                {/* Render Condo Polygons (Horizontal Condominiums) */}
                {mapData && mapData.condoPolygons && mapData.condoPolygons.map((condo, index) => {
                     if (!condo.coords || !Array.isArray(condo.coords)) return null;
                     const positions = condo.coords.map(c => [c.lat, c.lng]);

                     return (
                         <Polygon
                            key={`condo-${index}`}
                            positions={positions}
                            pathOptions={{ color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.15, weight: 2 }}
                         >
                            <Popup>
                                <strong>Condomínio Horizontal:</strong> {condo.name}
                            </Popup>
                         </Polygon>
                     );
                })}

                {/* Render Cables (Polylines) */}
                {mapData && mapData.cables && mapData.cables.map((cable, index) => {
                    let positions = [];
                    // Ozmap cables return a "poles" array containing objects with an "id" object holding the actual coords
                    if (cable.poles && Array.isArray(cable.poles)) {
                        positions = cable.poles.map(p => {
                            if (p.id && p.id.lat && p.id.lng) {
                                return [p.id.lat, p.id.lng];
                            } else if (p.id && Array.isArray(p.id.coords) && p.id.coords.length >= 2) {
                                return [p.id.coords[1], p.id.coords[0]]; // [lng, lat] to [lat, lng]
                            }
                            return null;
                        }).filter(pt => pt !== null);
                    }

                    if (positions.length < 2) return null;

                    let pathColor = '#3b82f6'; // default blue
                    if (cable.color) {
                        pathColor = typeof cable.color === 'object' && cable.color.color ? cable.color.color : cable.color;
                    }

                    return (
                        <Polyline
                            key={`cable-${index}`}
                            positions={positions}
                            pathOptions={{ color: pathColor, weight: 2, opacity: 0.7 }}
                        >
                            <Popup>
                                <strong>Cabo:</strong> {cable.name}<br/>
                                <span className="text-xs">ID: {cable.id}</span>
                            </Popup>
                        </Polyline>
                    );
                })}

                {/* Render Poles (Markers) */}
                {/* Render Poles BEFORE Boxes so Boxes appear on top (higher Z-order in SVG) */}
                {mapData && mapData.poles && mapData.poles.map((pole, index) => {
                     if (!pole.coords || !pole.coords.lat || !pole.coords.lng) return null;

                     return (
                         <CircleMarker
                            key={`pole-${index}`}
                            center={[pole.coords.lat, pole.coords.lng]}
                            radius={4}
                            pathOptions={{
                                color: '#64748b',
                                fillColor: '#94a3b8',
                                fillOpacity: 0.8,
                                weight: 1
                            }}
                         >
                            <Popup>
                                <strong>Poste</strong><br/>
                                <span className="text-xs text-slate-500">ID: {pole.id}</span>
                            </Popup>
                         </CircleMarker>
                     );
                })}

                {/* Render Buildings (Vertical Condos) */}
                {mapData && mapData.buildings && mapData.buildings.map((building, index) => {
                     if (!building.lat || !building.lng) return null;

                     // Building icon
                     const buildingIcon = L.divIcon({
                         html: `<div style="background-color: #3b82f6; width: 24px; height: 24px; border-radius: 4px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg></div>`,
                         className: '',
                         iconSize: [24, 24],
                         iconAnchor: [12, 12],
                         popupAnchor: [0, -12]
                     });

                     return (
                         <Marker
                            key={`building-${index}`}
                            position={[building.lat, building.lng]}
                            icon={buildingIcon}
                         >
                            <Popup>
                                <strong>Condomínio Vertical:</strong> {building.name}<br/>
                                {building.address && <span className="text-xs">{building.address}</span>}
                            </Popup>
                         </Marker>
                     );
                })}

                {/* Render Boxes (Markers) */}
                {uniqueBoxes.map((box, index) => {
                     if (!box.coords || !box.coords.lat || !box.coords.lng) return null;

                     const isNearest = effectiveCto &&
                                       box.name === effectiveCto.name &&
                                       Math.abs(box.coords.lat - effectiveCto.coords.lat) < 0.0001 &&
                                       Math.abs(box.coords.lng - effectiveCto.coords.lng) < 0.0001;

                     let isCto = false;
                     if (box.name && box.name.toUpperCase().includes('CTO')) isCto = true;
                     if (!isCto && box.boxType && box.boxType.name && box.boxType.name.toUpperCase().includes('CTO')) isCto = true;

                     let isImplantado = true;
                     if (isCto) {
                         isImplantado = box.implanted === true;
                     }

                     let isCeo = false;
                     if (box.name && box.name.toUpperCase().includes('CEO')) isCeo = true;
                     if (!isCeo && box.boxType && box.boxType.name && box.boxType.name.toUpperCase().includes('CEO')) isCeo = true;

                     const boxTypeStr = isCto ? 'CTO' : (isCeo ? 'CEO' : 'Caixa');

                     let fillColor = '#808080';
                     if (isCto) {
                         fillColor = isImplantado ? '#FFFF00' : '#808080'; // Yellow for implanted CTO, gray otherwise
                     } else if (isCeo) {
                         fillColor = '#00FFFF'; // Cyan for CEO
                     } else if (box.color) {
                         fillColor = typeof box.color === 'object' && box.color.color ? box.color.color : box.color;
                     }

                     let distanceStr = 'N/A';
                     if (box.distance) {
                         distanceStr = Math.round(box.distance) + 'm';
                     } else if (userLocation) {
                         const distance = L.latLng(userLocation).distanceTo(L.latLng([box.coords.lat, box.coords.lng]));
                         distanceStr = Math.round(distance) + 'm';
                     }

                     const isSelectableInSwapMode = routeMode === 'swap' && isCto && isImplantado;

                     return (
                         <CircleMarker
                            key={`box-${index}`}
                            center={[box.coords.lat, box.coords.lng]}
                            radius={isNearest ? 12 : 9}
                            pathOptions={{
                                fillColor: fillColor,
                                color: isNearest ? '#FF00FF' : fillColor, // Magenta border if nearest
                                weight: isNearest ? 4 : 2,
                                fillOpacity: 0.9,
                                opacity: 1,
                                className: isSelectableInSwapMode ? 'cto-selectable' : undefined,
                            }}
                            eventHandlers={isSelectableInSwapMode ? {
                                click: () => handleCtoSelect(box),
                            } : undefined}
                         >
                            {exportMode ? (
                                <Tooltip permanent direction="top" offset={[0, 2]} className="coverage-box-label">
                                    {box.name}
                                </Tooltip>
                            ) : (
                                <Popup>
                                    <strong>{boxTypeStr}{isNearest ? ' (Selecionada)' : ''}:</strong> {box.name}<br/>
                                    {!isImplantado && isCto && <span className="text-xs text-red-500 font-bold block">Status: Indisponível (Não Implantado)</span>}
                                    <strong>Distância:</strong> {distanceStr}
                                    {isSelectableInSwapMode && <span className="text-xs text-pink-500 font-bold block mt-1">Clique para selecionar esta CTO</span>}
                                </Popup>
                            )}
                         </CircleMarker>
                     );
                })}

            </MapContainer>

            {/* Context Menu */}
            {contextMenu.visible && ReactDOM.createPortal(
                <>
                    <div className="fixed inset-0 z-[1059]" onClick={closeContextMenu} />
                    <div
                        className="fixed z-[1060] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-56 overflow-hidden"
                        style={{ top: contextMenu.y, left: contextMenu.x }}
                    >
                        {darkMapEnabled && <button onClick={toggleMapMode} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <Palette className="h-4 w-4 text-green-500" />
                            Trocar modo de Cor
                        </button>}
                        <button onClick={handleScreenshot} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <Camera className="h-4 w-4 text-purple-500" />
                            Screenshot
                        </button>
                        {onRequestStreetView && (
                            <button onClick={handleStreetView} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                                <MapPin className="h-4 w-4 text-blue-500" />
                                Ver no Street View
                            </button>
                        )}
                        <button
                            onClick={handleExportProject}
                            disabled={!userLocation || !effectiveCto || isExporting}
                            className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <FileDown className="h-4 w-4 text-orange-500" />
                            Exportar Projeto
                        </button>
                    </div>
                </>,
                document.body
            )}

            {/* Submenu da linha do drop (clique direito na linha) */}
            {lineContextMenu.visible && ReactDOM.createPortal(
                <>
                    <div className="fixed inset-0 z-[1059]" onClick={closeLineContextMenu} />
                    <div
                        className="fixed z-[1060] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-52 overflow-hidden"
                        style={{ top: lineContextMenu.y, left: lineContextMenu.x }}
                    >
                        <button onClick={startSwapCto} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <ArrowLeftRight className="h-4 w-4 text-pink-500" />
                            Trocar CTO
                        </button>
                        <button onClick={startEditRoute} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <Waypoints className="h-4 w-4 text-purple-500" />
                            Alterar traçado
                        </button>
                    </div>
                </>,
                document.body
            )}

            {/* Banner de modo ativo (Trocar CTO / Alterar traçado) */}
            {routeMode !== 'idle' && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center gap-3 max-w-[90%]">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        {routeMode === 'swap'
                            ? 'Clique em uma CTO para trocar...'
                            : 'Clique na linha para criar um ponto. Arraste para ajustar. Segure Shift e clique num ponto para excluí-lo.'}
                    </span>
                    {routeMode === 'edit' && (
                        <button
                            onClick={finishEditRoute}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium whitespace-nowrap"
                        >
                            <Check className="h-3.5 w-3.5" />
                            Concluir edição
                        </button>
                    )}
                    <button
                        onClick={cancelRouteMode}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-medium whitespace-nowrap"
                    >
                        <IconX className="h-3.5 w-3.5" />
                        Cancelar
                    </button>
                </div>
            )}

            {/* Overlay de exportação de PDF — via portal para document.body, fora de mapRef,
                para nunca ser capturado pelo html2canvas do screenshot do mapa. */}
            {isExporting && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[1080] bg-black/40 flex items-center justify-center">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl px-5 py-4 flex items-center gap-3">
                        <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Gerando PDF do projeto...</span>
                    </div>
                </div>,
                document.body
            )}

            {/* Feedback Toast */}
            {feedback.visible && ReactDOM.createPortal(
                <div
                    className="fixed z-[1070] bg-black/80 text-white px-3 py-1 rounded-md text-sm pointer-events-none transition-opacity duration-300"
                    style={{ top: feedback.y - 40, left: feedback.x }}
                >
                    {feedback.message}
                </div>,
                document.body
            )}
        </div>
    );
};

export default CoverageMap;
